/**
 * JSON Document Editor Page
 * 
 * New document editor using JSON/AST model with TipTap.
 * This replaces the HTML-based editor with structured editing.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { StructuredEditor } from '../components/DocumentEditor/StructuredEditor';
import { DiffView } from '../components/DocumentEditor/DiffView';
import { documentsApi, companyDocumentsApi } from '../services/api';
import { usePageContext } from '../hooks/usePageContext';
import { useAI } from '../contexts/AIContext';
import { useNavigationSafe } from '../contexts/NavigationContext';
import { generatePreviewDocument } from '../utils/document-preview-generator';
import { offlineBackup } from '../services/offline-backup';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { createInsertSectionOperation } from '../utils/section-operations';
import { convertTipTapToDocument } from '../utils/tiptap-to-document';
import { logger } from '../utils/logger';
import type { AeroDocument } from '../types/document-schema';
import type { DocumentOperation } from '../types/document-operations';
import type { Editor } from '@tiptap/react';
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui';

export default function JsonDocumentEditorPage() {
  const { projectId, documentNumber, documentId } = useParams<{
    projectId?: string;
    documentNumber?: string;
    documentId?: string;
  }>();
  
  const isCompanyDocument = !!documentId;
  const isProjectDocument = !!projectId && !!documentNumber;
  
  const navigate = useNavigate();
  const nav = useNavigationSafe();
  
  // Use navigation context for back, with fallback
  const handleBack = () => {
    if (nav?.canGoBack) {
      nav.goBack();
    } else {
      // Fallback: navigate to appropriate list
      if (isCompanyDocument) {
        navigate('/company-documents');
      } else if (isProjectDocument) {
        navigate(`/projects/${projectId}`);
      } else {
        navigate('/workspace');
      }
    }
  };
  
  // Helper to get document key for localStorage
  const getDocKey = () => isCompanyDocument 
    ? `company-${documentId}` 
    : `${projectId}-${documentNumber}`;
  
  const editorRef = useRef<Editor | null>(null);
  const [document, setDocument] = useState<AeroDocument | null>(null);
  const [workingDocument, setWorkingDocument] = useState<AeroDocument | null>(null);
  const [baselineDocument, setBaselineDocument] = useState<AeroDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [pendingOperations, setPendingOperations] = useState<DocumentOperation[]>([]);
  const [_operationExplanation, setOperationExplanation] = useState<string>('');
  const [isApplyingOperations, setIsApplyingOperations] = useState(false);
  const [viewMode, setViewMode] = useState<'edit' | 'diff'>('edit');
  const [previewDocument, setPreviewDocument] = useState<AeroDocument | null>(null);
  const [splitViewEnabled, setSplitViewEnabled] = useState(false); // User can toggle split view on/off
  const [s3JsonKey, setS3JsonKey] = useState<string | null>(null); // S3 key for saving document
  
  // Auto-save state
  const isOnline = useOnlineStatus();
  const [lastSavedJson, setLastSavedJson] = useState<string>('');
  
  // Recovery modal state
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [recoveredDocument, setRecoveredDocument] = useState<AeroDocument | null>(null);
  
  // Derived: track whether there are unsaved changes (for beforeunload warning)
  const hasUnsavedChanges = workingDocument ? JSON.stringify(workingDocument) !== lastSavedJson : false;
  
  // Get AI context to listen for operations and track pending edits
  const { setOnApplyEdits, addPendingEdit } = useAI();
  
  // Set page context for AI assistant
  usePageContext({
    pageName: 'JSON Document Editor',
    canEdit: document?.metadata.status === 'Draft' || document?.metadata.status === 'In Review',
    documentContext: document ? {
      documentNumber: document.metadata.documentNumber,
      projectId: document.metadata.projectId,
      currentRevision: document.metadata.currentRevision,
      status: document.metadata.status,
      docType: document.metadata.docType,
      // CRITICAL: s3JsonKey is required for AI to save document edits to S3
      s3JsonKey: s3JsonKey,
      // CRITICAL: These flags enable semantic_search_document tool
      chunkCount: 16,  // Assume indexed (updated from backend data)
      indexStatus: 'completed',  // Assume indexed
      // For JSON documents, provide structure info
      jsonDocument: document,
      format: 'json',
      sectionCount: document.body.length,
    } : undefined,
  });
  
  // Load document
  useEffect(() => {
    loadDocument();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadDocument is stable callback
  }, [projectId, documentNumber, documentId]);
  
  // Set baseline document on initial load
  useEffect(() => {
    if (document && !baselineDocument) {
      setBaselineDocument(document);
      setWorkingDocument(document);
      setLastSavedJson(JSON.stringify(document));
    }
  }, [document, baselineDocument]);
  
  // Warn about unsaved changes before navigating away
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);
  
  // Check for crash recovery on load
  useEffect(() => {
    if (!document) return;
    if (!isProjectDocument && !isCompanyDocument) return;
    
    const docKey = getDocKey();
    const session = offlineBackup.getEditSession(docKey);
    
    if (session) {
      try {
        const backup = JSON.parse(session.content) as AeroDocument;
        // Check if backup is different from server version
        if (backup.version !== document.version || session.lastModified > document.lastModified) {
          setRecoveredDocument(backup);
          setShowRecoveryModal(true);
        }
      } catch (error) {
        logger.error('Failed to parse backup document:', error);
        // Clear corrupted backup
        offlineBackup.clearEditSession(docKey);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getDocKey, isCompanyDocument, isProjectDocument are stable
  }, [document, projectId, documentNumber]);
  
  // localStorage backup: continuously backup to localStorage (online or offline)
  useEffect(() => {
    if (!document) return;
    if (!isProjectDocument && !isCompanyDocument) return;
    
    const backupInterval = setInterval(() => {
      const docKey = `${projectId}-${documentNumber}`;
      const currentDoc = workingDocument || document;
      const currentJson = JSON.stringify(currentDoc);
      
      // Always backup to localStorage (whether online or offline)
      if (currentJson !== lastSavedJson) {
        offlineBackup.saveEditSession(docKey, currentJson, {
          documentNumber,
          projectId,
        });
      }
    }, 2000); // Backup every 2 seconds

    return () => clearInterval(backupInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isCompanyDocument, isProjectDocument are stable
  }, [document, workingDocument, lastSavedJson, projectId, documentNumber]);

  // Network autosave: save to server when online
  useEffect(() => {
    // Only run network autosave if online and document loaded
    if (!isOnline || !document) return;
    if (!isProjectDocument && !isCompanyDocument) return;
    
    const autoSaveInterval = setInterval(async () => {
      const currentDoc = workingDocument || document;
      const currentJson = JSON.stringify(currentDoc);
      
      // If content differs from last saved and we're not already saving
      if (currentJson !== lastSavedJson && !saving) {
        try {
          setSaving(true);
          
          if (isCompanyDocument) {
            // Save company document (policy/template)
            await companyDocumentsApi.update(documentId!, {
              content: JSON.stringify(currentDoc, null, 2),
              jsonDocument: currentDoc
            });
          } else {
            // Save project document
            await documentsApi.saveContent(projectId!, documentNumber!, '', {
              jsonDocument: currentDoc
            });
          }
          
          setLastSavedJson(currentJson);
          setLastSaved(new Date());
          
          // Clear localStorage backup after successful save
          const docKey = getDocKey();
          offlineBackup.clearEditSession(docKey);
        } catch (error: unknown) {
          // Check for AI edit conflict (409 with currentDocument)
          const axiosError = error as { response?: { status?: number; data?: { currentDocument?: AeroDocument } } };
          if (axiosError.response?.status === 409 && axiosError.response?.data?.currentDocument) {
            logger.debug('[AutoSave] AI edit conflict detected - syncing with server version');
            const serverDoc = axiosError.response.data.currentDocument;
            
            // Update to server's version (AI's edits take precedence)
            setDocument(serverDoc);
            setWorkingDocument(serverDoc);
            setLastSavedJson(JSON.stringify(serverDoc));
            setLastSaved(new Date());
            
            // No error shown to user - this is expected during AI editing
            return;
          }
          
          logger.error('Auto-save failed:', error);
          // Continue - localStorage backup is still active
        } finally {
          setSaving(false);
        }
      }
    }, 5000); // Check every 5 seconds when online

    return () => clearInterval(autoSaveInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Various document-type checkers are stable
  }, [isOnline, document, workingDocument, lastSavedJson, saving]);
  
  // Register handler for AI-generated operations
  useEffect(() => {
    interface AIOperationItem {
      isJsonOps?: boolean;
      operations?: DocumentOperation[];
      op?: string;
      targetId?: string;
      sectionId?: string;
      newBlock?: {
        id?: string;
        type?: string;
        content?: unknown;
      };
    }
    const handleAIOperations = (operations: AIOperationItem[], messageContent: string) => {
      logger.debug('[JsonDocumentEditor] ========== AI OPERATIONS RECEIVED ==========');
      logger.debug('[JsonDocumentEditor] operations array:', operations);
      logger.debug('[JsonDocumentEditor] operations.length:', operations.length);
      logger.debug('[JsonDocumentEditor] messageContent:', messageContent.substring(0, 100));
      logger.debug('[JsonDocumentEditor] Full operations object:', JSON.stringify(operations, null, 2));
      
      if (operations.length === 0) {
        logger.warn('[JsonDocumentEditor] Empty operations array received');
        return;
      }
      
      // Check if operations are JSON operations (vs HTML edits)
      logger.debug('[JsonDocumentEditor] First operation:', operations[0]);
      logger.debug('[JsonDocumentEditor] Is JSON ops:', operations[0]?.isJsonOps);
      logger.debug('[JsonDocumentEditor] First op keys:', Object.keys(operations[0]));
      
      if (operations[0]?.isJsonOps) {
        const ops = operations[0].operations || [];
        logger.debug('[JsonDocumentEditor] ========== EXTRACTED OPERATIONS ==========');
        logger.debug('[JsonDocumentEditor] Operations count:', ops.length);
        ops.forEach((op: DocumentOperation, i: number) => {
          logger.debug(`[JsonDocumentEditor] Op ${i + 1}:`, {
            op: op.op,
            targetId: op.targetId,
            sectionId: op.sectionId,
            newBlock: op.newBlock ? {
              id: op.newBlock.id,
              type: op.newBlock.type,
              hasContent: !!op.newBlock.content
            } : undefined,
            explanation: op.explanation
          });
        });
        
        // Check if document is loaded
        if (!document) {
          logger.error('[JsonDocumentEditor] ❌ Cannot generate preview - document not loaded');
          return;
        }
        
        logger.debug('[JsonDocumentEditor] Current document state:', {
          documentNumber: document.metadata.documentNumber,
          sectionCount: document.body.length,
          totalBlocks: document.body.reduce((sum, s) => sum + s.blocks.length, 0)
        });
        
        // Generate preview document
        logger.debug('[JsonDocumentEditor] Calling generatePreviewDocument...');
        const preview = generatePreviewDocument(document, ops);
        logger.debug('[JsonDocumentEditor] Preview generated, setting state...');
        setPreviewDocument(preview);
        setPendingOperations(ops);
        setOperationExplanation(operations[0].explanation || messageContent);
        setViewMode('diff'); // Switch to diff view
        logger.debug('[JsonDocumentEditor] ✅ Switched to diff view');
      } else {
        logger.warn('[JsonDocumentEditor] Received non-JSON operations, checking structure...');
        logger.warn('[JsonDocumentEditor] Operation keys:', Object.keys(operations[0] || {}));
        
        // Maybe operations are already unwrapped?
        if (operations[0]?.op) {
          logger.debug('[JsonDocumentEditor] Operations appear to be unwrapped, using directly');
          setPendingOperations(operations);
          setOperationExplanation(messageContent);
        }
      }
    };
    
    setOnApplyEdits(() => handleAIOperations);
    
    return () => {
      setOnApplyEdits(undefined);
    };
  }, [setOnApplyEdits, document]); // Add document to dependencies so handler updates when document loads
  
  const loadDocument = async () => {
    // Validate we have either project document or company document params
    if (!isProjectDocument && !isCompanyDocument) {
      setError('Missing document identifiers');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      let doc: AeroDocument;
      
      if (isCompanyDocument) {
        // Load company document (policy or template)
        const response = await companyDocumentsApi.get(documentId!, true);
        doc = response.jsonDocument;
        // Extract s3JsonKey from company document response (from current revision)
        const currentRev = response.revisions?.find((r: { revision: string; s3JsonKey?: string }) => r.revision === response.currentRevision);
        if (currentRev?.s3JsonKey) {
          setS3JsonKey(currentRev.s3JsonKey);
          logger.debug('[JsonDocumentEditorPage] Loaded company document s3JsonKey:', currentRev.s3JsonKey);
        } else if (response.s3JsonKey) {
          setS3JsonKey(response.s3JsonKey);
          logger.debug('[JsonDocumentEditorPage] Loaded company document s3JsonKey (top-level):', response.s3JsonKey);
        }
      } else {
        // Load project document with metadata (including s3JsonKey for AI to save)
        const result = await documentsApi.loadAsJsonWithMetadata(projectId!, documentNumber!);
        doc = result.jsonDocument!;
        if (result.s3JsonKey) {
          setS3JsonKey(result.s3JsonKey);
          logger.debug('[JsonDocumentEditorPage] Loaded project document s3JsonKey:', result.s3JsonKey);
        }
      }
      
      setDocument(doc);
      
    } catch (err: unknown) {
      logger.error('Error loading document:', err);
      setError(err.message || 'Failed to load document');
    } finally {
      setLoading(false);
    }
  };
  
  const handleWorkingCopyUpdate = (updatedDoc: AeroDocument) => {
    setWorkingDocument(updatedDoc);
    // Auto-save will trigger automatically via useEffect
  };
  
  const handleUpdate = useCallback((_operations: DocumentOperation[]) => {
    // Convert TipTap content to AeroDocument JSON so auto-save can detect changes
    if (editorRef.current && document) {
      const updatedDoc = convertTipTapToDocument(editorRef.current, document);
      setWorkingDocument(updatedDoc);
    }
  }, [document]);
  
  const handleAcceptSelectedOperations = async (selectedIndices: number[]) => {
    if (!document || selectedIndices.length === 0) {
      logger.error('[JsonDocumentEditor] Cannot accept - missing required data');
      return;
    }
    
    // Operations API only works for project documents currently
    if (isCompanyDocument) {
      logger.warn('Operations not yet supported for company documents - use auto-save');
      return;
    }
    
    if (!projectId || !documentNumber) {
      logger.error('[JsonDocumentEditor] Missing project/document identifiers');
      return;
    }
    
    // Get only the selected operations
    const selectedOps = selectedIndices.map(i => pendingOperations[i]);
    
    try {
      setIsApplyingOperations(true);
      setError(null);
      
      logger.debug('[JsonDocumentEditor] Applying selected operations:', {
        projectId,
        documentNumber,
        operationsCount: selectedOps.length,
        baseVersion: document.version,
        operations: selectedOps
      });
      
      const result = await documentsApi.applyOperations(
        projectId,
        documentNumber,
        selectedOps,
        document.version
      );
      
      logger.debug('[JsonDocumentEditor] Operations result:', result);
      
      // API wraps response in 'data' field
      const responseData = result.data || result;
      
      if (responseData.success && responseData.document) {
        logger.debug('[JsonDocumentEditor] SUCCESS - Updating document state');
        logger.debug('[JsonDocumentEditor] New version:', responseData.document.version);
        setDocument(responseData.document);
        setWorkingDocument(responseData.document);
        setLastSavedJson(JSON.stringify(responseData.document));
        
        // Track pending operations for optimistic indexing
        if (projectId && documentNumber) {
          addPendingEdit({
            documentNumber,
            projectId,
            operations: selectedOps,
            appliedAt: new Date(),
            version: responseData.document.version
          });
          logger.debug('[JsonDocumentEditor] Tracked', selectedOps.length, 'pending operations for optimistic search');
        }
        
        setPendingOperations([]);
        setOperationExplanation('');
        setPreviewDocument(null);
        setViewMode('edit'); // Switch back to edit mode
        logger.debug('[JsonDocumentEditor] Document state updated, returning to edit mode');
      } else {
        const errorMsg = responseData.error || 'Failed to apply operations';
        logger.error('[JsonDocumentEditor] Operation failed:', errorMsg);
        setError(errorMsg);
      }
      
    } catch (err: unknown) {
      logger.error('[JsonDocumentEditor] Error applying operations:', err);
      logger.error('[JsonDocumentEditor] Error details:', err.response?.data);
      setError(err.response?.data?.error || err.message || 'Failed to apply operations');
    } finally {
      setIsApplyingOperations(false);
    }
  };
  
  const handleRejectOperations = () => {
    setPendingOperations([]);
    setOperationExplanation('');
    setPreviewDocument(null);
    setViewMode('edit'); // Switch back to edit mode
  };
  
  const handleCatchupBaseline = () => {
    // Update baseline to match current working copy
    const currentDoc = workingDocument || document;
    if (currentDoc) {
      setBaselineDocument(currentDoc);
    }
  };
  
  const handleAddSection = async (
    sectionType: 'section' | 'subsection' | 'appendix',
    title: string,
    parentId?: string
  ) => {
    if (!document) {
      logger.error('[JsonDocumentEditor] Cannot add section - no document loaded');
      return;
    }
    
    // Operations API only for project documents
    if (isCompanyDocument) {
      logger.warn('Section operations not yet supported for company documents');
      return;
    }
    
    if (!projectId || !documentNumber) {
      logger.error('[JsonDocumentEditor] Missing project/document identifiers');
      return;
    }
    
    try {
      // Find the last section (or parent's last child) to insert after
      let targetSectionId: string | undefined;
      
      if (parentId) {
        // Find the parent section
        const parentSection = document.body.find(s => s.id === parentId);
        if (parentSection && parentSection.children.length > 0) {
          // Insert after last child
          const lastChildId = parentSection.children[parentSection.children.length - 1];
          targetSectionId = lastChildId;
        } else {
          // Parent has no children, insert as first child
          targetSectionId = parentId;
        }
      } else {
        // Insert at top level - find last top-level section
        const topLevelSections = document.body.filter(s => !s.parent);
        if (topLevelSections.length > 0) {
          targetSectionId = topLevelSections[topLevelSections.length - 1].id;
        }
      }
      
      // Create insert section operation
      const operation = createInsertSectionOperation(
        sectionType,
        title,
        targetSectionId,
        'after',
        parentId
      );
      
      // Apply operation
      const result = await documentsApi.applyOperations(
        projectId,
        documentNumber,
        [operation],
        document.version
      );
      
      const responseData = result.data || result;
      
      if (responseData.success && responseData.document) {
        setDocument(responseData.document);
        setWorkingDocument(responseData.document);
        setLastSavedJson(JSON.stringify(responseData.document));
      } else {
        const errorMsg = responseData.error || 'Failed to add section';
        setError(errorMsg);
      }
      
    } catch (err: unknown) {
      logger.error('[JsonDocumentEditor] Error adding section:', err);
      setError(err.response?.data?.error || err.message || 'Failed to add section');
    }
  };
  
  const handleUseRecoveredVersion = () => {
    if (recoveredDocument && projectId && documentNumber) {
      setWorkingDocument(recoveredDocument);
      setDocument(recoveredDocument);
      setShowRecoveryModal(false);
      setRecoveredDocument(null);
    }
  };
  
  const handleDiscardRecoveredVersion = () => {
    const docKey = getDocKey();
    offlineBackup.clearEditSession(docKey);
    setShowRecoveryModal(false);
    setRecoveredDocument(null);
  };
  
  // No manual save needed - auto-save handles everything
  // This is kept for compatibility but does nothing
  const handleSave = async () => {
    logger.debug('Manual save called, but auto-save is active');
  };
  
  // Handle document sync from server (after AI edits)
  // This is CRITICAL for preventing stale document state after agentic editing
  const handleDocumentSyncFromServer = useCallback((syncedDocument: AeroDocument, _s3VersionId?: string, _preAIVersionId?: string) => {
    logger.debug('[JsonDocumentEditor] Document sync from server, version:', syncedDocument.version);
    
    // Update all document state to match server
    setDocument(syncedDocument);
    setWorkingDocument(syncedDocument);
    
    // Update lastSavedJson to prevent auto-save from overwriting with stale data
    const syncedJson = JSON.stringify(syncedDocument);
    setLastSavedJson(syncedJson);
    
    // Update lastSaved timestamp to reflect server save
    setLastSaved(new Date());
    
    logger.debug('[JsonDocumentEditor] Document state synchronized with server');
  }, []);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="loading">Loading document...</div>
      </div>
    );
  }
  
  if (error && !document) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="error">
          <h2>Error Loading Document</h2>
          <p>{error}</p>
          <Button onClick={() => navigate(-1)} className="mt-4">
            Go Back
          </Button>
        </div>
      </div>
    );
  }
  
  if (!document) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="info-box">
          Document not found
        </div>
      </div>
    );
  }
  
  // Determine if showing diff view
  const shouldShowDiffView = (viewMode === 'diff' && previewDocument) || splitViewEnabled;
  const docToCompare = previewDocument || document;
  const hasAIOperations = pendingOperations.length > 0;
  
  // DEBUG: Log render state
  if (viewMode === 'diff' || pendingOperations.length > 0) {
    logger.debug('[JsonDocumentEditor] ========== RENDER STATE ==========');
    logger.debug('[JsonDocumentEditor] viewMode:', viewMode);
    logger.debug('[JsonDocumentEditor] previewDocument:', !!previewDocument);
    logger.debug('[JsonDocumentEditor] pendingOperations:', pendingOperations.length);
    logger.debug('[JsonDocumentEditor] shouldShowDiffView:', shouldShowDiffView);
    logger.debug('[JsonDocumentEditor] hasAIOperations:', hasAIOperations);
    
    if (previewDocument) {
      logger.debug('[JsonDocumentEditor] Preview document blocks:', 
        previewDocument.body.reduce((sum, s) => sum + s.blocks.length, 0));
    }
    if (document) {
      logger.debug('[JsonDocumentEditor] Original document blocks:', 
        document.body.reduce((sum, s) => sum + s.blocks.length, 0));
    }
  }
  
  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Content area - editors handle their own headers */}
      <div className="flex-1 min-h-0">
        {shouldShowDiffView ? (
          <DiffView
            originalDocument={baselineDocument || document}
            previewDocument={docToCompare}
            operations={pendingOperations}
            onAcceptSelected={hasAIOperations ? handleAcceptSelectedOperations : undefined}
            onRejectAll={hasAIOperations ? handleRejectOperations : undefined}
            isApplying={isApplyingOperations}
            onCloseSplitView={() => {
              setSplitViewEnabled(false);
              if (hasAIOperations) {
                handleRejectOperations();
              }
            }}
            showOperationsSidebar={hasAIOperations}
            onToggleSplitView={() => setSplitViewEnabled(!splitViewEnabled)}
            splitViewEnabled={splitViewEnabled}
            onWorkingCopyUpdate={handleWorkingCopyUpdate}
            onCatchupBaseline={handleCatchupBaseline}
            onAddSection={handleAddSection}
            documentTitle={`${document.metadata.documentNumber} - ${document.metadata.projectSpecificTitle}`}
            onBack={handleBack}
            saving={saving}
            error={error}
            lastSaved={lastSaved}
            isOnline={isOnline}
          />
        ) : (
          <StructuredEditor
            document={workingDocument || document}
            onUpdate={handleUpdate}
            onSave={handleSave}
            editorRef={editorRef}
            documentTitle={`${document.metadata.documentNumber} - ${document.metadata.projectSpecificTitle}`}
            onBack={handleBack}
            saving={saving}
            error={error}
            hasUnsavedChanges={false}
            onToggleSplitView={() => setSplitViewEnabled(!splitViewEnabled)}
            splitViewEnabled={splitViewEnabled}
            lastSaved={lastSaved}
            isOnline={isOnline}
            onAddSection={handleAddSection}
            projectId={projectId}
            documentNumber={documentNumber}
            onDocumentSyncFromServer={handleDocumentSyncFromServer}
          />
        )}
      </div>
      
      {/* Crash Recovery Modal */}
      <Dialog open={showRecoveryModal && !!recoveredDocument} onOpenChange={(open) => !open && handleDiscardRecoveredVersion()}>
        <DialogContent className="max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Recover Unsaved Changes?</DialogTitle>
          </DialogHeader>
          <p className="text-foreground mb-4">
            We found unsaved changes from a previous editing session. Would you like to recover them?
          </p>
          <div className="bg-muted p-4 rounded mb-6 text-sm">
            <div className="flex justify-between mb-2">
              <span className="font-semibold text-foreground">Document:</span>
              <span className="text-muted-foreground">{document?.metadata.documentNumber}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span className="font-semibold text-foreground">Server version:</span>
              <span className="text-muted-foreground">{new Date(document?.lastModified || '').toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-semibold text-foreground">Recovered version:</span>
              <span className="text-muted-foreground">{recoveredDocument && new Date(recoveredDocument.lastModified).toLocaleString()}</span>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={handleDiscardRecoveredVersion}
            >
              Discard Recovery
            </Button>
            <Button
              variant="success"
              onClick={handleUseRecoveredVersion}
            >
              Use Recovered Version
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
